import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../store";
import { logout } from "../store/slices/authSlice";
import { toggleSidebar } from "../store/slices/uiSlice";
import { authService } from "../services/authService";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications,
  Search,
  Person,
  Settings,
  Logout,
  Brightness4,
  Brightness7,
  KeyboardArrowDown,
} from "@mui/icons-material";
import CompanyLogo from "./common/CompanyLogo";
import { ThemeToggle } from "../theme/ThemeToggle";
import { capitalizeWords, formatStatusText } from "../utils/formatters";
import { UPLOAD_BASE_PATH } from "../config/upload";
import { config } from "../config/environment";

// Helper function to construct avatar URL from filename
const getAvatarUrl = (avatarUrl?: string | null): string | undefined => {
  if (!avatarUrl) return undefined;
  
  // If it's already a full URL, return as is
  if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
    return avatarUrl;
  }
  
  // If it's a full path starting with /uploads, construct URL
  if (avatarUrl.startsWith('/uploads/')) {
    const baseUrl = config.API_URL.replace(/\/api\/?$/, '');
    return `${baseUrl}${avatarUrl}`;
  }
  
  // Otherwise, it's just a filename - construct full URL
  const baseUrl = config.API_URL.replace(/\/api\/?$/, '');
  return `${baseUrl}${UPLOAD_BASE_PATH}/${avatarUrl}`;
};

const TopNavbar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Get avatar URL - check both avatar and avatarUrl fields
  const avatarUrl = user?.avatar || user?.avatarUrl;
  const fullAvatarUrl = getAvatarUrl(avatarUrl);
  const { sidebarCollapsed } = useSelector((state: RootState) => state.ui);
  const [notifications] = useState(3); // Mock notifications
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      dispatch(logout());
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Still logout locally even if API call fails
      dispatch(logout());
      navigate("/login");
    }
    handleMenuClose();
  };

  const handleProfileClick = () => {
    navigate("/profile");
    handleMenuClose();
  };

  const handleSettingsClick = () => {
    navigate("/settings");
    handleMenuClose();
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        color: "text.primary",
      }}
    >
      <Toolbar 
        sx={{ 
          px: { xs: 2, sm: 3, lg: 4 },
          minHeight: '64px !important',
          height: '64px',
        }}
      >
        {/* Left side */}
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          {/* Mobile sidebar toggle and logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <IconButton
                onClick={handleToggleSidebar}
                sx={{
                  display: { lg: "none" },
                  color: "text.secondary",
                  bgcolor: "rgba(59, 130, 246, 0.1)",
                  p: 1.5,
                  "&:hover": {
                    bgcolor: "rgba(59, 130, 246, 0.2)",
                    color: "primary.main",
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </motion.div>

            {/* Mobile logo - visible when sidebar is collapsed */}
            {/* {sidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <CompanyLogo
                  size="sm"
                  variant="minimal"
                  animated={false}
                  showTagline={false}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "#000000",
                    fontSize: "1.1rem",
                    whiteSpace: "nowrap",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {config.BRANCH_NAME}10100
                </Typography>
              </motion.div>
            )} */}
          </Box>

          {/* Company Logo - visible on desktop, larger when sidebar collapsed */}
          <Box sx={{ 
            display: { xs: "none", lg: "flex" }, 
            alignItems: "center",
            gap: 1.5,
            mr: sidebarCollapsed ? 2 : 3,
            transition: "all 0.3s ease"
          }}>
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <CompanyLogo
                size={sidebarCollapsed ? "lg" : "md"}
                variant="minimal"
                animated={false}
                showTagline={false}
              />
            </motion.div>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "#000000",
                fontSize: "1.1rem",
                whiteSpace: "nowrap",
              }}
            >
              {config.BRANCH_NAME}
            </Typography>
          </Box>

          {/* Search bar - hidden on mobile */}
          {/* <Box
            sx={{
              display: { xs: "none", sm: "block" },
              flex: 1,
              maxWidth: 400,
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <TextField
                placeholder="Search anything..."
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "text.secondary", fontSize: 22 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    borderRadius: 3,
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    "&:hover": {
                      bgcolor: "rgba(255, 255, 255, 0.9)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                    },
                    "&.Mui-focused": {
                      bgcolor: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(59, 130, 246, 0.5)",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                    },
                  },
                }}
              />
            </motion.div>
          </Box> */}
        </Box>

        {/* Right side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Theme toggle */}
          {/* <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ThemeToggle />
          </motion.div> */}

          {/* Notifications */}
          {/* <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Tooltip title="Notifications">
              <IconButton 
                sx={{ 
                  color: "text.secondary",
                  bgcolor: "rgba(59, 130, 246, 0.1)",
                  p: 1.5,
                  "&:hover": {
                    bgcolor: "rgba(59, 130, 246, 0.2)",
                    color: "primary.main",
                  },
                }}
              >
                <Badge 
                  badgeContent={notifications} 
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: '0.75rem',
                      height: 18,
                      minWidth: 18,
                    }
                  }}
                >
                  <Notifications sx={{ fontSize: 24 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          </motion.div> */}

          {/* User profile section */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Box
              onClick={handleMenuClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1,
                borderRadius: 3,
                cursor: "pointer",
                bgcolor: "rgba(59, 130, 246, 0.05)",
                border: "1px solid rgba(59, 130, 246, 0.1)",
                "&:hover": {
                  bgcolor: "rgba(59, 130, 246, 0.1)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {/* User Avatar */}
              {fullAvatarUrl ? (
                <Avatar
                  src={fullAvatarUrl}
                  alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
                  sx={{ 
                    width: 40, 
                    height: 40,
                    border: "2px solid rgba(59, 130, 246, 0.2)",
                  }}
                />
              ) : (
                <Avatar 
                  sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: "primary.main",
                    border: "2px solid rgba(59, 130, 246, 0.2)",
                  }}
                >
                  <Person sx={{ fontSize: 22 }} />
                </Avatar>
              )}

              {/* User info - hidden on mobile */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  variant="body2"
                  sx={{ 
                    fontWeight: 600, 
                    lineHeight: 1.2,
                    color: "text.primary",
                  }}
                >
                  {capitalizeWords( user ? `${user.firstName} ${user.lastName}` : "User")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Chip
                    label={ formatStatusText(user?.role || "User")}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      bgcolor: "rgba(59, 130, 246, 0.1)",
                      color: "primary.main",
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Box>

              {/* Dropdown arrow */}
              <KeyboardArrowDown 
                sx={{ 
                  fontSize: 22, 
                  color: "text.secondary",
                  transition: "transform 0.2s ease",
                  transform: anchorEl ? "rotate(180deg)" : "rotate(0deg)",
                }} 
              />
            </Box>
          </motion.div>

          {/* User menu dropdown */}
          <AnimatePresence>
            {Boolean(anchorEl) && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 240,
                      borderRadius: 3,
                      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.1)",
                      overflow: "hidden",
                    },
                  }}
                >
                  {/* User info header */}
                  <Box sx={{ p: 2, bgcolor: "rgba(59, 130, 246, 0.05)", borderBottom: "1px solid rgba(59, 130, 246, 0.1)" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
                      {user ? `${user.firstName} ${user.lastName}` : "User"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {user?.email || "user@example.com"}
                    </Typography>
                  </Box>

                  <MenuItem 
                    onClick={handleProfileClick}
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        bgcolor: "rgba(59, 130, 246, 0.08)",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Person sx={{ color: "primary.main", fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Profile" 
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </MenuItem>

                  <MenuItem 
                    onClick={handleSettingsClick}
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        bgcolor: "rgba(59, 130, 246, 0.08)",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Settings sx={{ color: "primary.main", fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Settings" 
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  <MenuItem 
                    onClick={handleLogout}
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        bgcolor: "rgba(220, 38, 38, 0.08)",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Logout sx={{ color: "error.main", fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Sign out" 
                      primaryTypographyProps={{ 
                        fontWeight: 500,
                        color: "error.main",
                      }}
                    />
                  </MenuItem>
                </Menu>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNavbar;
